// ═══════════════════════════════════════
// MODAL — Gestionnaire de modales dynamiques
//
// Principe :
//   openModal(viewKey, onMounted)
//     → fetch /api/modals.php?view={viewKey}&lang={lang}
//     → injecte le HTML dans #modal-content
//     → affiche #modal-overlay
//     → appelle onMounted(navigate, controller)
//        - navigate(viewKey) : charge une autre vue dans la même modale ouverte
//        - controller        : AbortController pour annuler les listeners en fin de vie
//
//   closeModal()
//     → masque l'overlay
//     → annule tous les AbortControllers actifs
//     → vide #modal-content après la transition CSS
//
// Les attributs data-goto="viewKey" sur les boutons sont gérés
// automatiquement sans nécessiter de code dans onMounted.
// ═══════════════════════════════════════

import { lang } from './i18n.js';

// Controller global à la modale ouverte (overlay, Escape)
let _overlayController = null;

// Controller propre à la vue courante (listeners métier)
let _viewController = null;

// Callback onMounted fourni par l'appelant, réutilisé à chaque navigate()
let _onMounted = null;

// Cache session : clé = "viewKey:lang" pour éviter les collisions entre langues
const _cache = new Map();

// ─────────────────────────────────────────
// openModal — ouvre la modale avec une vue
// ─────────────────────────────────────────
export async function openModal(viewKey, onMounted = null) {
    const overlay = document.getElementById('modal-overlay');
    const content = document.getElementById('modal-content');
    if (!overlay || !content) {
        console.error('[modal] #modal-overlay ou #modal-content introuvable dans le DOM');
        return;
    }

    _onMounted         = onMounted;
    _overlayController = new AbortController();
    const { signal }   = _overlayController;

    content.innerHTML = '<div class="modal-loading">…</div>';
    overlay.classList.add('active');

    // ⚠️ Différé d'un tick pour laisser le clic déclencheur terminer son bubbling
    setTimeout(() => {
        if (_overlayController?.signal.aborted) return;

        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) closeModal();
        }, { signal });

        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') closeModal();
        }, { signal });
    }, 0);

    await _loadView(viewKey);
}

// ─────────────────────────────────────────
// navigate — charge une autre vue dans la modale déjà ouverte
// ─────────────────────────────────────────
export async function navigate(viewKey) {
    if (!_overlayController || _overlayController.signal.aborted) return;
    _viewController?.abort();
    _viewController = null;
    await _loadView(viewKey);
}

// ─────────────────────────────────────────
// closeModal — ferme et nettoie la modale
// ─────────────────────────────────────────
export function closeModal() {
    const overlay = document.getElementById('modal-overlay');
    if (overlay) overlay.classList.remove('active');

    _viewController?.abort();
    _overlayController?.abort();
    _viewController    = null;
    _overlayController = null;
    _onMounted         = null;

    const content = document.getElementById('modal-content');
    if (content) {
        setTimeout(() => { content.innerHTML = ''; }, 300);
    }
}

// ─────────────────────────────────────────
// isModalOpen
// ─────────────────────────────────────────
export function isModalOpen() {
    return _overlayController !== null;
}

// ─────────────────────────────────────────
// _loadView — fetch + injection + listeners
// ─────────────────────────────────────────
async function _loadView(viewKey) {
    const content = document.getElementById('modal-content');
    if (!content) return;

    _viewController = new AbortController();
    const { signal } = _viewController;

    _overlayController?.signal.addEventListener('abort', () => {
        _viewController?.abort();
    }, { once: true });

    // Clé de cache incluant la langue pour éviter les collisions FR/EN
    const currentLang = lang();
    const cacheKey    = `${viewKey}:${currentLang}`;

    try {
        let html;
        if (_cache.has(cacheKey)) {
            html = _cache.get(cacheKey);
        } else {
            const res = await fetch(
                `/api/modals.php?view=${encodeURIComponent(viewKey)}&lang=${encodeURIComponent(currentLang)}`,
                { signal: _overlayController?.signal }
            );
            if (!res.ok) throw new Error(`Vue introuvable : ${viewKey}`);
            html = await res.text();
            _cache.set(cacheKey, html);
        }

        content.innerHTML = html;

        _bindGoto(signal);

        if (_onMounted) {
            await Promise.resolve(_onMounted(navigate, _viewController));
        }

    } catch (e) {
        if (e.name === 'AbortError') return;
        console.error('[modal] Erreur chargement vue :', viewKey, e);
        content.innerHTML = '<p style="color:var(--danger,#e74c3c);text-align:center;padding:20px">Erreur de chargement.</p>';
    }
}

// ─────────────────────────────────────────
// _bindGoto — délégation des data-goto
// ─────────────────────────────────────────
function _bindGoto(signal) {
    document.getElementById('modal-content')
        ?.querySelectorAll('[data-goto]')
        .forEach(el => {
            el.addEventListener('click', (e) => {
                e.preventDefault();
                navigate(el.dataset.goto);
            }, { signal });
        });
}